/**
 * What a redline card carries, in the two shapes the add-in can apply.
 *
 * - `ooxml`: the redline as tracked-change markup, the shape prod's Python
 *   writer produces. The add-in reads two strings per paragraph out of it,
 *   the desired text and the text before the author touched it, and runs
 *   `ProposalBuilder` itself.
 * - `texts`: those strings directly, one entry per paragraph of the bookmarked
 *   range in order, plus the new paragraphs asked for and the row and table
 *   changes (`tableChanges`) the add-in writes after the paragraphs. This is
 *   what the core emits when the proposal engine flag is on: nothing wrote
 *   markup for it.
 *
 * `redlineTextCardSchema` is the tool result the core's `redline_section`
 * emits for a text card; `MessageItem` validates it before rendering.
 */
import { z } from "zod";

import { type TableOperationsApplyResult, tableOperationsApplyResultSchema } from "../table-operations";
import { linkInstructionSchema } from "./link-instruction";

/**
 * The paragraph above or below a card paragraph as the document read when the card
 * was built: its Word ids and its accepted text. The tracker cuts the context words
 * out of the text with the engine's tokenizer and sends the ids as the bracket a
 * moved paragraph is looked for between.
 *
 * NULL
 *   `paraId` and `textId` are null when the document carries no `w14:paraId` /
 *   `w14:textId` on that paragraph.
 *
 * WHY an absent id reads as null
 *   A live review sends its Issues with their null fields left out, so the anchor of
 *   an Issue's comment arrives without them; read as null, it is the anchor the core
 *   wrote.
 */
export const cardParagraphNeighbourSchema = z.object({
    paraId: z.string().nullable().default(null),
    textId: z.string().nullable().default(null),
    text: z.string(),
});
export type CardParagraphNeighbour = z.infer<typeof cardParagraphNeighbourSchema>;

/**
 * Where a card paragraph sat in the document when the card was built, read off the
 * core's document read (`redline.card_anchors`): its Word ids, the heading path over
 * it, its index among the body paragraphs, its position as a percentage of the
 * document (0 to 100, the locator's scale), and the paragraphs either side of it. A
 * fresh tracker primes its memory from it before any read (`AnchorMemory`), so a
 * reopened copy of the same file resolves by id, then by neighbours and heading,
 * before any model is asked.
 *
 * NULL
 *   `headingPath` for a paragraph before any heading; `neighbourAbove` at the
 *   document's first paragraph and `neighbourBelow` at its last.
 */
export const cardParagraphAnchorSchema = z.object({
    /** An absent nullable field reads as null: a live review sends its Issues with their null fields left out (`cardParagraphNeighbourSchema`). */
    paraId: z.string().nullable().default(null),
    textId: z.string().nullable().default(null),
    headingPath: z.array(z.string()).nullable().default(null),
    paragraphIndex: z.number().int(),
    positionPct: z.number(),
    neighbourAbove: cardParagraphNeighbourSchema.nullable().default(null),
    neighbourBelow: cardParagraphNeighbourSchema.nullable().default(null),
});
export type CardParagraphAnchor = z.infer<typeof cardParagraphAnchorSchema>;

export const redlineTextParagraphSchema = z.object({
    /** The `_dioptra_p_*` bookmark naming the paragraph, or null for a paragraph in the range that carries none. */
    bookmark: z.string().nullable(),
    textProposed: z.string(),
    textWhenProposed: z.string(),
    /**
     * Where the paragraph sat when the card was built (`cardParagraphAnchorSchema`).
     *
     * NULL, or absent
     *   A card stored before anchors were carried (2026-10-16), a regenerated card
     *   whose previous card carried none, a paragraph the core's read did not hold,
     *   and a paragraph the add-in builds itself (a comment's paragraph as it reads):
     *   the tracker then anchors by text, as before.
     */
    anchor: cardParagraphAnchorSchema.nullable().optional(),
    /**
     * What becomes of each link whose words the card changes (`LinkInstruction`), which
     * the engine that writes the card follows (`RedlineEngine`) and the card shows.
     *
     * EMPTY, or absent
     *   The card changes no link's words, was stored before instructions were carried,
     *   or is a paragraph the add-in builds itself (a comment's paragraph as it reads):
     *   the engine keeps or strikes a link as it did before.
     */
    linkInstructions: z.array(linkInstructionSchema).optional(),
});

/**
 * A card paragraph as the records written before 2026-09-22 spell it: `desiredText`
 * for `textProposed`, `baselineText` for `textWhenProposed`.
 *
 * WHEN a stored chat turn's redline card or a live-review report's `textRedline`
 * predates the rename
 * CONSEQUENCE it still parses, and comes out in the current spelling, so nothing
 * past this schema sees the old keys.
 */
const legacyRedlineTextParagraphSchema = z
    .object({ bookmark: z.string().nullable(), desiredText: z.string(), baselineText: z.string() })
    .transform(({ bookmark, desiredText, baselineText }): z.infer<typeof redlineTextParagraphSchema> => ({ bookmark, textProposed: desiredText, textWhenProposed: baselineText }));

/**
 * The union a stored paragraph is read through. A stored record carries no version
 * tag, so the key it uses is the discriminant: the current spelling is tried first,
 * a record without `textProposed` falls to the legacy branch. Zod's
 * `discriminatedUnion` needs a literal tag, which these records never had.
 */
const storedRedlineTextParagraphSchema = z.union([redlineTextParagraphSchema, legacyRedlineTextParagraphSchema]);

/**
 * What a new paragraph is to the list the paragraph it follows belongs to, chosen
 * by the model: "continue" makes it one more item, with its neighbour's numbering,
 * style and indent; "none" makes it a plain paragraph in the document's default
 * style, the shape of a closing sentence after the last item.
 */
export const paragraphListContinuationSchema = z.enum(["continue", "none"]);
export type ParagraphListContinuation = z.infer<typeof paragraphListContinuationSchema>;

export const redlineTextInsertionSchema = z.object({
    afterBookmark: z.string(),
    /**
     * The new paragraph's own id, given when it was proposed: the applier writes the
     * paragraph bookmarked with it, a comment the card carries on the new paragraph
     * names it, and Go to issue finds the applied paragraph by it.
     *
     * NULL, or absent
     *   A card proposed before new paragraphs carried an id: the paragraph is written
     *   unbookmarked, and found after its anchor as before.
     */
    bookmark: z.string().nullish(),
    textProposed: z.string(),
    /** Whether the new paragraph continues its neighbour's list or stands plain (`ParagraphListContinuation`). */
    list: paragraphListContinuationSchema,
    /**
     * Where the new paragraph will sit, built by the core from the anchor of the
     * paragraph it follows (`new_paragraph_anchors`): that paragraph's heading path, the
     * index the new paragraph takes, and its neighbours. A comment on the new paragraph
     * is found again by it when Word drops the paragraph's id.
     *
     * NULL, or absent
     *   The paragraph it follows carried no anchor on the card, or the card was stored
     *   before new paragraphs carried one: the comment is found by its id and text alone.
     */
    anchor: cardParagraphAnchorSchema.nullish(),
});

/**
 * A card insertion as the pre-2026-09-22 records spell it: `desiredText` for `textProposed`. Same WHEN and CONSEQUENCE as the legacy paragraph.
 *
 * NULL `bookmark` and `anchor`: those records predate new paragraphs carrying either.
 */
const legacyRedlineTextInsertionSchema = z
    .object({ afterBookmark: z.string(), desiredText: z.string(), list: paragraphListContinuationSchema })
    .transform(({ afterBookmark, desiredText, list }) => ({ afterBookmark, bookmark: null, textProposed: desiredText, list, anchor: null }));

/** The union a stored insertion is read through; discriminated by key presence like the paragraph's. */
const storedRedlineTextInsertionSchema = z.union([redlineTextInsertionSchema, legacyRedlineTextInsertionSchema]);

/**
 * What a text card is, stamped by the core when it emits the card:
 *   - `paragraphs` -> the card edits paragraph text, and may add paragraphs
 *   - `table`      -> the card also, or only, changes a table: rows added or removed,
 *                     or a table inserted; only Word's table API can write it
 * The one value Apply All routes a card on, so no reader inspects `tableChanges`.
 */
export const cardKindSchema = z.enum(["paragraphs", "table"]);
export type CardKind = z.infer<typeof cardKindSchema>;

/**
 * The kind a card stored before `cardKind` existed (2026-09-23) reads as, from
 * the table changes it carries. The stored reader below is its only caller; a
 * card the core emits today carries its kind.
 */
export const cardKindOfTableChanges = (tableChanges: TableOperationsApplyResult): CardKind => (carriesTableOperations(tableChanges) ? "table" : "paragraphs");

/**
 * True when the card asks for a row or a table: an operation only the applier's
 * table writer can carry out, whatever the card's label says.
 */
export const carriesTableOperations = (tableChanges: TableOperationsApplyResult): boolean =>
    tableChanges.rowInsertions.length + tableChanges.rowDeletions.length + tableChanges.tableInsertions.length > 0;

/** What a pending tracked change is: words an author added, or words an author struck. */
export const pendingChangeKindSchema = z.enum(["ins", "del"]);

/**
 * A pending change as the agent read it when it asked for the revert: kind and
 * author as the reader's tag says them, text as the change's words read (the
 * struck words of a deletion, the surviving words of an insertion). The engine
 * asks again for the revert when the id no longer names a change reading like this.
 */
export const expectedPendingChangeSchema = z.object({
    kind: pendingChangeKindSchema,
    author: z.string(),
    text: z.string(),
});

/**
 * One revert the card carries: the paragraph, the reader's id of the change in
 * it, the change as it was read, and, for a deletion, the words the restored
 * text should read instead of exactly as they were (null for as they were).
 * `ProposalBuilder` applies a paragraph's reverts before its text redline.
 */
export const redlineTextRevertSchema = z.object({
    bookmark: z.string(),
    changeId: z.string(),
    expectedChange: expectedPendingChangeSchema,
    text: z.string().nullable(),
});

const redlineTextProposalFields = {
    author: z.string().min(1),
    paragraphs: z.array(storedRedlineTextParagraphSchema),
    insertions: z.array(storedRedlineTextInsertionSchema),
    /** The row and table changes the apply route accepted (`TableOperationsApplyResult`), written after the paragraphs. */
    tableChanges: tableOperationsApplyResultSchema,
    /** The pending changes the card reverts (`revert_tracked_change`), empty on every other card. */
    reverts: z.array(redlineTextRevertSchema),
};

/**
 * The card as a stored record spells it: `cardKind` is missing on a record written
 * before it existed, and the reader stamps it from the table changes; `reverts` is
 * missing on a record written before reverts existed, and reads as none.
 *
 * WHEN a stored chat turn's card or a live-review remedy's `textRedline` predates
 * `cardKind` or `reverts`
 * CONSEQUENCE it still parses, with its kind stamped and no reverts, so nothing
 * past this schema reads a card without either.
 */
const storedRedlineTextProposalSchema = z.object({ ...redlineTextProposalFields, cardKind: cardKindSchema.optional(), reverts: redlineTextProposalFields.reverts.optional() });

export const redlineTextProposalSchema = storedRedlineTextProposalSchema.transform((card) => ({
    ...card,
    cardKind: card.cardKind ?? cardKindOfTableChanges(card.tableChanges),
    reverts: card.reverts ?? [],
}));

export type PendingChangeKind = z.infer<typeof pendingChangeKindSchema>;
export type ExpectedPendingChange = z.infer<typeof expectedPendingChangeSchema>;
export type RedlineTextRevert = z.infer<typeof redlineTextRevertSchema>;
export type RedlineTextParagraph = z.infer<typeof redlineTextParagraphSchema>;
export type RedlineTextInsertion = z.infer<typeof redlineTextInsertionSchema>;
export type RedlineTextProposal = z.infer<typeof redlineTextProposalSchema>;

/** A payload is never stored; it is built from a parsed card (`textsPayloadFrom`), so its kind is always present. */
export const redlinePayloadSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("ooxml"), redlineOoxml: z.string().min(1), author: z.string().min(1) }),
    z.object({ ...redlineTextProposalFields, cardKind: cardKindSchema, kind: z.literal("texts") }),
]);

export type RedlinePayload = z.infer<typeof redlinePayloadSchema>;
export type OoxmlRedlinePayload = Extract<RedlinePayload, { kind: "ooxml" }>;
export type TextsRedlinePayload = Extract<RedlinePayload, { kind: "texts" }>;

/**
 * One comment the agent attached to a card's change, placed by the core on a
 * paragraph the card writes (`card_comments_of`). The add-in writes it once the card
 * has landed, never without it.
 *
 * CASES, by the paragraph the comment sits on
 *   - a paragraph the card edits -> `bookmark` names it; "span" on `anchor_text`, or "first_word"
 *   - a paragraph the card adds  -> `bookmark` is the new paragraph's own id (`RedlineTextInsertion.bookmark`),
 *                                   which the applier writes it under; "span" or "first_word" as above
 *   - a paragraph the card adds, proposed by an engine that gave it no id
 *                                -> `bookmark` names the paragraph it follows, "after_paragraph"
 *
 * NULL
 *   `anchor_text` is null unless the comment sits on some of the paragraph's words.
 *
 * NULL, or absent
 *   `comment_id` is the id the core minted when it created the comment, by which the
 *   add-in keeps the user's edit of it and whether it is left out. A card stored before
 *   comments carried ids reads null: its comments are written with the card as they
 *   came, and cannot be edited or left out one by one.
 */
export const cardCommentSchema = z.object({
    text: z.string(),
    bookmark: z.string(),
    placement: z.enum(["first_word", "span", "after_paragraph"]),
    anchor_text: z.string().nullable(),
    comment_id: z.string().nullish().transform((commentId) => commentId ?? null),
});

export type CardComment = z.infer<typeof cardCommentSchema>;

/**
 * The comments a card's tool result carries.
 *
 * FALLBACK
 *   A card emitted before cards carried comments has no `comments` key, and reads as
 *   carrying none.
 */
export const cardCommentsSchema = z.array(cardCommentSchema).optional().transform((comments) => comments ?? []);

export const redlineTextCardSchema = z.object({
    redline: redlineTextProposalSchema,
    bookmarks: z.array(z.string()),
    comments: cardCommentsSchema,
});

export type RedlineTextCard = z.infer<typeof redlineTextCardSchema>;

/** The `texts` payload for a text proposal, the one place the discriminant is stamped on. */
export function textsPayloadFrom(proposal: RedlineTextProposal): TextsRedlinePayload {
    return { kind: "texts", author: proposal.author, paragraphs: proposal.paragraphs, insertions: proposal.insertions, tableChanges: proposal.tableChanges, cardKind: proposal.cardKind, reverts: proposal.reverts };
}
